/* Pure helpers behind the Stats Nutrition view: the 28-day bars, the tiles,
 * the weekly table, top foods and the "use as calorie target" patch. Day
 * status, averages and adherence come from logic/energy (shared with the MCP
 * server), so a "complete day" means the same here as in Claude's summaries.
 * Nothing reads the clock: the tab passes todayKey() in. */

import { dailyRollup, nutritionDay, weeklyAdherence } from '../logic/energy';
import { mondayOf } from '../logic/schedule';
import { addDays, dayNum } from './statsData';

export const NUTRITION_DAYS = 28;

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function num(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** Profile targets as numbers (0 when unset). */
export function nutritionTargets(profile) {
  const p = isObj(profile) ? profile : {};
  const m = isObj(p.macros) ? p.macros : {};
  return { kcal: num(p.calorieTarget), protein: num(m.protein), carbs: num(m.carbs), fats: num(m.fats) };
}

/**
 * How a day's bar is drawn. 'empty' (nothing logged), 'untracked' (marked
 * not tracked), 'partial' (not a complete day), else for kcal 'on' (within
 * +-10% of the target), 'under' or 'over'; for protein 'on' (>= 90% of the
 * target) or 'under'. The same thresholds as weeklyAdherence.
 * @param {{items:number, status:string, kcal:number, protein:number}} row a dailyRollup row
 * @param {'kcal'|'protein'} metric
 * @param {{kcal:number, protein:number}} targets
 */
export function dayBand(row, metric, targets) {
  if (!row || !row.items) return 'empty';
  if (row.status === 'untracked') return 'untracked';
  if (row.status !== 'complete') return 'partial';
  if (metric === 'protein') {
    if (!(targets.protein > 0)) return 'on';
    return row.protein * 10 >= targets.protein * 9 ? 'on' : 'under';
  }
  if (!(targets.kcal > 0)) return 'on';
  if (Math.abs(row.kcal - targets.kcal) * 10 <= targets.kcal) return 'on';
  return row.kcal < targets.kcal ? 'under' : 'over';
}

function mean(rows, field) {
  return rows.reduce((s, r) => s + r[field], 0) / rows.length;
}

/**
 * The days behind the bars and tiles: the `days` days ending yesterday
 * (today is never complete, so it never pulls an average down).
 * @param {object} state reads nutrition, logStatus, profile, bodyMetrics, logs
 * @param {string} today YYYY-MM-DD
 * @param {{days?: number}} [opts]
 * @returns {null|{from:string, to:string, days:number, targets:object,
 *   rows:Array<{date, kcal, protein, carbs, fats, items, status, band, proteinBand}>,
 *   loggedDays:number, recent:{days:number, from:string, kcal:number, protein:number}|null}}
 *   loggedDays = complete days with entries; recent = averages over the
 *   last 7 of them (fewer when there aren't 7)
 */
export function nutritionOverview(state, today, opts = {}) {
  if (dayNum(today) === null) return null;
  const days = opts.days || NUTRITION_DAYS;
  const st = isObj(state) ? state : {};
  const targets = nutritionTargets(st.profile);
  const from = addDays(today, -days);
  const to = addDays(today, -1);
  const rows = dailyRollup(st, from, to, { targets }).map(r => ({
    date: r.date, kcal: r.kcal, protein: r.protein, carbs: r.carbs, fats: r.fats, items: r.items, status: r.status,
    band: dayBand(r, 'kcal', targets), proteinBand: dayBand(r, 'protein', targets),
  }));
  const complete = rows.filter(r => r.status === 'complete' && r.items > 0);
  const last = complete.slice(-7);
  return {
    from, to, days, targets, rows,
    loggedDays: complete.length,
    recent: last.length
      ? { days: last.length, from: last[0].date, kcal: Math.round(mean(last, 'kcal')), protein: Math.round(mean(last, 'protein')) }
      : null,
  };
}

/**
 * Consecutive days with protein >= 90% of the target, ending today. Today
 * only counts once it is hit, so an unfinished day never breaks the streak
 * (the supplementStreak rule).
 * @param {object} nutrition state.nutrition
 * @param {string} today YYYY-MM-DD
 * @param {number} target protein g
 * @returns {{current:number, todayHit:boolean}}
 */
export function proteinStreak(nutrition, today, target) {
  if (!(target > 0) || dayNum(today) === null) return { current: 0, todayHit: false };
  const hit = key => {
    const entries = isObj(nutrition) ? nutrition[key] : null;
    return Array.isArray(entries) && entries.length > 0 && nutritionDay(entries).protein * 10 >= target * 9;
  };
  const todayHit = hit(today);
  let cursor = todayHit ? today : addDays(today, -1);
  let current = 0;
  while (hit(cursor)) {
    current += 1;
    cursor = addDays(cursor, -1);
  }
  return { current, todayHit };
}

/**
 * The last `weeks` calendar weeks (Mon-Sun) up to yesterday, newest first.
 * The current week only has the days that have passed (`days` < 7).
 * @returns {Array} weeklyAdherence rows
 */
export function weekTable(state, today, weeks = 8) {
  const to = addDays(today, -1);
  if (!to) return [];
  const from = addDays(mondayOf(to), -7 * (weeks - 1));
  return weeklyAdherence(isObj(state) ? state : {}, from, to).slice(-weeks).reverse();
}

/**
 * Foods that supply the most kcal (or protein) over from..to, grouped by
 * lowercase name.
 * @param {object} nutrition state.nutrition
 * @param {string} from YYYY-MM-DD
 * @param {string} to YYYY-MM-DD
 * @param {'kcal'|'protein'} metric
 * @param {number} [limit]
 * @returns {{total:number, rows:Array<{key, name, kcal, protein, count, share}>}}
 *   name = the latest spelling; count = entries logged; share = part of the
 *   window's total of `metric` (0..1)
 */
export function topFoods(nutrition, from, to, metric = 'kcal', limit = 8) {
  const groups = new Map();
  let total = 0;
  const dates = Object.keys(isObj(nutrition) ? nutrition : {})
    .filter(d => dayNum(d) !== null && d >= from && d <= to)
    .sort();
  dates.forEach(date => {
    const entries = Array.isArray(nutrition[date]) ? nutrition[date] : [];
    entries.forEach(e => {
      if (!isObj(e) || typeof e.name !== 'string' || !e.name.trim()) return;
      const name = e.name.trim();
      const key = name.toLowerCase();
      const m = nutritionDay([e]);
      const g = groups.get(key) || { key, name, kcal: 0, protein: 0, count: 0 };
      g.name = name;
      g.kcal += m.kcal;
      g.protein += m.protein;
      g.count += 1;
      groups.set(key, g);
      total += m[metric];
    });
  });
  const rows = [...groups.values()]
    .filter(g => g[metric] > 0)
    .sort((a, b) => b[metric] - a[metric] || a.key.localeCompare(b.key))
    .slice(0, limit)
    .map(g => ({
      ...g,
      kcal: Math.round(g.kcal),
      protein: Math.round(g.protein * 10) / 10,
      share: total > 0 ? g[metric] / total : 0,
    }));
  return { total, rows };
}

/**
 * Profile patch for a new calorie target. Protein and fats stay fixed and
 * carbs absorb the difference (4 kcal per g), so the macros keep adding up.
 * Without a current target the macros are left alone.
 * @param {object} profile
 * @param {number} kcal new calorie target
 * @returns {{calorieTarget:number, macros:object}}
 */
export function calorieTargetPatch(profile, kcal) {
  const p = isObj(profile) ? profile : {};
  const macros = isObj(p.macros) ? p.macros : {};
  const current = num(p.calorieTarget);
  if (!(current > 0)) return { calorieTarget: kcal, macros: { ...macros } };
  const carbs = Math.max(0, Math.round(num(macros.carbs) + (kcal - current) / 4));
  return { calorieTarget: kcal, macros: { ...macros, carbs } };
}

/* Why estimateEnergyBalance left a day out, in words. */
export const EXCLUDED_REASONS = {
  'marked-partial': 'marked partial',
  'marked-untracked': 'marked not tracked',
  'no-entries': 'nothing logged',
  'under-half-target': 'under half the target',
  'few-items': 'fewer than 3 items',
  'mostly-estimates': 'mostly estimates',
};

export const GOAL_LABELS = {
  'build muscle': 'Build muscle',
  'lose fat': 'Lose fat',
  recomp: 'Body recomp',
  strength: 'Strength',
  maintenance: 'Maintenance',
};

/** 2940 -> '2,940' (the app's English UI, whatever the phone's locale). */
export function fmtInt(v) {
  return Math.round(Number(v) || 0).toLocaleString('en-GB');
}

/** '+0.12', '−0.40', '±0.00' with a real minus sign. */
export function signed(v, digits = 2) {
  const r = Number((Number(v) || 0).toFixed(digits));
  return `${r > 0 ? '+' : r < 0 ? '−' : '±'}${Math.abs(r).toFixed(digits)}`;
}
